import { useState } from "react";
import Button from "./Button";
import { formatThaiDate } from "../utils/date";

/**
 * Helper: Check if two dates are the same calendar day
 */
const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    return (
        d1.getDate() === d2.getDate() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getFullYear() === d2.getFullYear()
    );
};

export default function DateRangePickerPopover({
    startDate,
    endDate,
    onApply,
    onCancel,
}) {
    // 1. Initialize View State
    const [viewDate, setViewDate] = useState(() => {
        const date = startDate || new Date();
        return new Date(date.getFullYear(), date.getMonth(), 1);
    });

    const [tempStart, setTempStart] = useState(startDate);
    const [tempEnd, setTempEnd] = useState(endDate);
    const [hoverDate, setHoverDate] = useState(null);
    const today = new Date();

    // 2. Navigation Handlers
    const prevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    const nextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));

    // 3. Generate Fixed 42 Days (6 Rows x 7 Cols)
    const generateMonthData = (year, month) => {
        const firstDay = new Date(year, month, 1);
        const startDayOfWeek = firstDay.getDay(); // 0 = Sunday

        // Start from the Sunday before the 1st
        const curr = new Date(firstDay);
        curr.setDate(curr.getDate() - startDayOfWeek);

        const days = [];
        for (let i = 0; i < 42; i++) {
            days.push(new Date(curr));
            curr.setDate(curr.getDate() + 1);
        }
        return days;
    };

    // 4. Selection Handler
    const handleDateClick = (date) => {
        // Reset if we have both or neither
        if ((tempStart && tempEnd) || !tempStart) {
            setTempStart(date);
            setTempEnd(null);
            return;
        }
        // If clicking before start, reset start
        if (date < tempStart) {
            setTempStart(date);
            setTempEnd(null);
            return;
        }
        // If clicking same day, toggle (set as single day range)
        if (isSameDay(date, tempStart)) {
            setTempEnd(date);
            return;
        }
        // Set End
        setTempEnd(date);
    };

    // 5. Render Helper for Single Month
    const renderCalendarGrid = (baseDate) => {
        const currentYear = baseDate.getFullYear();
        const currentMonth = baseDate.getMonth();
        const days = generateMonthData(currentYear, currentMonth);

        // Header Text
        const fullThaiDate = formatThaiDate(baseDate, { month: "long", includeYear: true });
        const monthHeader = fullThaiDate.replace(/^[0-9]+\s/, "");

        return (
            <div className="w-[340px] px-2 flex flex-col">
                {/* --- Header --- */}
                <div className="flex flex-row items-center gap-3 justify-between relative select-none">
                    <Button
                        hierarchy="secondary"
                        size="md"
                        onClick={prevMonth}

                        icon="chevron-left"
                    />
                    <span className="text-lg font-medium text-[#2b2f3b]">
                        {monthHeader}
                    </span>
                    <Button
                        hierarchy="secondary"
                        size="md"
                        onClick={nextMonth}
                        icon="chevron-right"
                    />
                </div>

                {/* --- Days of Week --- */}
                <div className="grid grid-cols-7">
                    {["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"].map((d) => (
                        <div key={d} className="text-center text-body-small text-main-typography-system-description font-normal p-3 gap-3 select-none">
                            {d}
                        </div>
                    ))}
                </div>

                {/* --- Dates Grid --- */}
                {/* gap-y-1 adds a little breathing room between rows */}
                <div className="grid grid-cols-7">
                    {days.map((date, idx) => {
                        const isCurrentMonth = date.getMonth() === currentMonth;
                        const isToday = isCurrentMonth && isSameDay(date, today);

                        // Normalize all to start of day for safe comparison
                        const dTime = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
                        const normStart = tempStart ? new Date(tempStart.getFullYear(), tempStart.getMonth(), tempStart.getDate()) : null;
                        const normEnd = tempEnd ? new Date(tempEnd.getFullYear(), tempEnd.getMonth(), tempEnd.getDate()) : null;
                        const normHover = hoverDate ? new Date(hoverDate.getFullYear(), hoverDate.getMonth(), hoverDate.getDate()) : null;

                        const effectiveEnd = normEnd || (normHover && normStart && normHover > normStart ? normHover : null);

                        const sTime = normStart ? normStart.getTime() : null;
                        const eTime = effectiveEnd ? effectiveEnd.getTime() : null;

                        const isStart = sTime === dTime;
                        const isEnd = eTime === dTime;
                        const isInRange = sTime && eTime && dTime > sTime && dTime < eTime;

                        // --- Styling Logic ---

                        // 1. Text Style (The Circle)
                        // Fixed at w-[44px] h-[44px]
                        let textClass = "relative z-10 w-[44px] h-[44px] flex items-center justify-center rounded-full text-sm font-normal transition-all duration-75 ";
                        if (isStart || isEnd || isInRange) {
                            textClass += "text-white font-medium";
                        } else if (!isCurrentMonth) {
                            textClass += "text-main-typography-system-description group-hover:bg-gray-50";
                        } else {
                            textClass += "text-main-typography-system-default group-hover:bg-gray-100";
                        }

                        // 2. Background Strip Style (The Blue Shape)
                        // Height fixed to match container (44px)
                        let bgClass = "absolute inset-y-0 bg-creation-primary-solid-surface-focusing z-0 ";
                        let showBg = false;

                        if (isStart && isEnd) {
                            // Single day selected (Start == End)
                            // Width fixed at 44px to match the circle
                            bgClass += "left-0 right-0 w-[44px] mx-auto rounded-lg";
                            showBg = true;
                        } else if (isStart) {
                            // Start of range -> Round Left, fill Right
                            bgClass += "left-0 right-0 rounded-l-lg";
                            showBg = true;
                        } else if (isEnd) {
                            // End of range -> Round Right, fill Left
                            bgClass += "left-0 right-0 rounded-r-lg";
                            showBg = true;
                        } else if (isInRange) {
                            // Middle -> Full Rectangle
                            bgClass += "left-0 right-0";
                            showBg = true;
                        }

                        return (
                            <div
                                key={idx}
                                // Wrapper fixed at h-[44px] to ensure row height consistency
                                className="relative w-full h-[44px] p-0 flex items-center justify-center cursor-pointer select-none group"
                                onClick={() => handleDateClick(date)}
                                onMouseEnter={() => setHoverDate(date)}
                                onMouseLeave={() => setHoverDate(null)}
                            >
                                {/* Background Layer */}
                                {showBg && <div className={bgClass} />}

                                {/* Content Layer */}
                                <span
                                    className={textClass}
                                    aria-current={isToday ? "date" : undefined}
                                >
                                    {date.getDate()}
                                    {isToday && (
                                        <span
                                            aria-hidden="true"
                                            className="absolute bottom-1 left-1/2 h-3 w-3 -translate-x-1/2 rounded-full bg-creation-secondary-solid-surface-default"
                                        />
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const leftMonthDate = new Date(viewDate);
    const rightMonthDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);

    return (
        <div
            className="bg-white rounded-xl border border-main-border-system-default overflow-hidden w-fit max-w-[95vw] flex flex-col p-4 gap-4"
        >
            {/* Calendar Body */}
            <div className="flex p-4 gap-0 justify-center items-start">
                {renderCalendarGrid(leftMonthDate)}

                {/* Divider */}
                <div className="w-[1px] bg-gray-100 mx-6 self-stretch" />

                {renderCalendarGrid(rightMonthDate)}
            </div>

            {/* Horizontal Divider */}
            <div className="h-px bg-main-border-system-default mx-5" />

            {/* Footer */}
            <div className="flex items-center justify-between px-5">
                <div className="text-gray-500 font-normal text-lg truncate">
                    {tempStart ? (
                        <div className="flex gap-2">
                            <span>{formatThaiDate(tempStart, { month: "long", includeYear: true })}</span>
                            {tempEnd && (
                                <>
                                    <span>-</span>
                                    <span>{formatThaiDate(tempEnd, { month: "long", includeYear: true })}</span>
                                </>
                            )}
                        </div>
                    ) : (
                        <span className="opacity-50">เลือกวันเริ่มต้น - วันสิ้นสุด</span>
                    )}
                </div>

                <div className="flex gap-3">
                    <Button
                        hierarchy="secondary"
                        size="md"
                        text="ยกเลิก"
                        onClick={onCancel}
                        className="min-w-[120px]"
                    />
                    <Button
                        hierarchy="primary"
                        size="md"
                        text="ตกลง"
                        disabled={!tempStart || !tempEnd}
                        onClick={() => onApply(tempStart, tempEnd)}
                        className="min-w-[120px]"
                    />
                </div>
            </div>
        </div>
    );
}
